import { reactive, computed, unref } from 'vue';

// Delay before an outgoing message is actually sent, giving the agent a
// window to cancel it ("undo send"). Applies to all channels.
export const UNDO_SEND_MS = 15000;

// pending message id (echo_id) -> { expiresAt: number, cancel: () => void }
const pending = reactive({});

/**
 * Register an undo window for a pending message.
 * @param {string} id pending message id (echo_id)
 * @returns {Promise<boolean>} resolves true if cancelled, false after the delay
 */
export function registerUndoSend(id, delayMs = UNDO_SEND_MS) {
  if (!delayMs || delayMs <= 0) return Promise.resolve(false);
  return new Promise(resolve => {
    const timer = setTimeout(() => {
      delete pending[id];
      resolve(false);
    }, delayMs);
    pending[id] = {
      expiresAt: Date.now() + delayMs,
      cancel: () => {
        clearTimeout(timer);
        delete pending[id];
        resolve(true);
      },
    };
  });
}

export function cancelUndoSend(id) {
  const entry = pending[unref(id)];
  if (entry) entry.cancel();
}

/**
 * Reactive expiry timestamp (ms) for a message's undo window, or undefined.
 * @param {import('vue').Ref<string>|string} id
 */
export function useUndoSendExpiry(id) {
  return computed(() => pending[unref(id)]?.expiresAt);
}
